import React, { useState, useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import ReactPaginate from 'react-paginate'

import { getListCashBoxes } from '../../../../common/core/_requests'
import { ICashBox, CashBoxListParams } from '../../../../../types'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { KTIcon } from '../../../../../_metronic/helpers'
import { CreateCashBox } from '../../../../../components/CreateCashBox'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'

type SortField = 'id' | 'title' | 'enabled' | 'created_at'
type SortDirection = 'asc' | 'desc'

const CashboxesList: React.FC = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [cashboxes, setCashboxes] = useState<ICashBox[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('id')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  
  const perPage = 100

  const fetchCashboxes = async () => {
    setLoading(true)
    
    try {
      const params: CashBoxListParams = {
        page: currentPage,
        perPage
      }
      
      const response = await getListCashBoxes(params)
      
      if (response.data?.success && response.data.cashBoxes) {
        setCashboxes(response.data.cashBoxes)
        setTotalRows(response.data.totalRows || 0)
      }
    } catch (error) {
      console.error('Ошибка загрузки касс:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCashboxes()
  }, [currentPage])

  // Фильтрация и сортировка на клиенте
  const filteredAndSortedCashboxes = useMemo(() => {
    // Сначала фильтруем по поисковому запросу
    let filtered = cashboxes
    if (searchTerm) {
      filtered = cashboxes.filter(cashbox =>
        cashbox.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    // Затем сортируем
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any = a[sortField]
      let bValue: any = b[sortField]
      
      if (sortField === 'created_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1
      }
      return 0
    })
    
    return sorted
  }, [cashboxes, searchTerm, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleCreateCashBox = () => {
    setShowCreateModal(true)
  }

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
  }

  const handleCashBoxCreated = (newCashBox: ICashBox) => {
    // Обновляем список касс, добавляя новую кассу в начало (так как сортировка по ID DESC)
    setCashboxes(prev => [newCashBox, ...prev])
    setTotalRows(prev => prev + 1)
    setShowCreateModal(false)
  }

  const handleViewDetails = (cashboxId: number) => {
    navigate(`/settings/cashbox/${cashboxId}`)
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return 'switch' // нейтральная иконка когда сортировка не активна
    }
    return sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'
  }

  const getSortIconClass = (field: SortField) => {
    if (sortField !== field) {
      return 'text-muted' // серый цвет для неактивной сортировки
    }
    return 'text-primary' // синий цвет для активной сортировки
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru })
  }

  const getStatusBadge = (enabled: boolean) => {
    return (
      <span className={`badge ${enabled ? 'badge-success' : 'badge-danger'}`}>
        {enabled ? 'Активна' : 'Неактивна'}
      </span>
    )
  }

  // CPAYMENT-1546: operational block state
  const getBlockBadge = (blocked?: boolean) => {
    return (
      <span className={`badge ${blocked ? 'badge-danger' : 'badge-light-success'}`}>
        {blocked ? 'Заблокирована' : 'Нет'}
      </span>
    )
  }

  const countPages = Math.ceil(totalRows / perPage)

  return (
    <div>
      <div className="card sticky-top-tickets mb-7">
        <div className="card-header border-0 pt-5 pb-2">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Кассы</span>
            <span className="text-muted mt-1 fw-semibold fs-7">
              {searchTerm 
                ? `Найдено: ${filteredAndSortedCashboxes.length} из ${totalRows}` 
                : `Всего касс: ${totalRows}`
              }
            </span>
          </h3>
          <div className="card-toolbar">
            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <button
                className="btn btn-sm btn-primary"
                onClick={handleCreateCashBox}
              >
                <KTIcon iconName="plus" className="fs-2" />
                Создать кассу
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-toolbar">
            <div className="d-flex align-items-center position-relative my-1">
              <KTIcon iconName="magnifier" className="fs-1 position-absolute ms-4" />
              <input
                type="text"
                className="form-control form-control-solid w-250px ps-14"
                placeholder="Поиск по названию..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="card-body py-3">
          {loading ? (
            <div className="d-flex justify-content-center py-10">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Загрузка...</span>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHead>
                  <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                    <th 
                      className="cursor-pointer text-hover-primary user-select-none" 
                      onClick={() => handleSort('id')}
                    >
                      ID
                      <KTIcon 
                        iconName={getSortIcon('id')} 
                        className={`fs-7 ms-1 ${getSortIconClass('id')}`} 
                      />
                    </th>
                    <th 
                      className="cursor-pointer text-hover-primary user-select-none"
                      onClick={() => handleSort('title')}
                    >
                      Название
                      <KTIcon 
                        iconName={getSortIcon('title')} 
                        className={`fs-7 ms-1 ${getSortIconClass('title')}`} 
                      />
                    </th>
                    <th 
                      className="cursor-pointer text-hover-primary user-select-none"
                      onClick={() => handleSort('enabled')}
                    >
                      Статус
                      <KTIcon
                        iconName={getSortIcon('enabled')}
                        className={`fs-7 ms-1 ${getSortIconClass('enabled')}`}
                      />
                    </th>
                    <th className="user-select-none">Блокировка</th>
                    <th className="text-end user-select-none">Овердрафт</th>
                    <th className="text-end user-select-none">Баланс</th>
                    <th
                      className="cursor-pointer text-hover-primary user-select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      Дата создания
                      <KTIcon 
                        iconName={getSortIcon('created_at')}
                        className={`fs-7 ms-1 ${getSortIconClass('created_at')}`}
                      />
                    </th>
                    <th className="text-end min-w-100px">Действия</th>
                  </tr>
                </TableHead>
                <TableBody>
                  {filteredAndSortedCashboxes.map((cashbox: ICashBox) => (
                    <tr key={cashbox.id}>
                      <td>
                        <div className="text-dark fw-bold fs-6">
                          #{cashbox.id}
                        </div>
                      </td>
                      <td>
                        <div className="text-dark fw-bold">
                          {cashbox.title}
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(cashbox.enabled)}
                      </td>
                      <td>
                        {getBlockBadge(cashbox.blocked)}
                      </td>
                      <td className="text-end">
                        <span className="text-dark fw-semibold">
                          {cashbox.overdraft_limit ?? '—'}
                        </span>
                        {cashbox.enforce_balance && (
                          <div className="text-muted fs-8" title="Касса не берёт тикет больше остатка с учётом овердрафта">
                            учёт включён
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <span className={`fw-semibold ${cashbox.balance != null && parseFloat(cashbox.balance) < 0 ? 'text-danger' : 'text-dark'}`}>
                          {cashbox.balance ?? '—'}
                        </span>
                      </td>
                      <td>
                        <div className="text-muted fw-semibold">
                          {formatDate(cashbox.created_at)}
                        </div>
                      </td>
                      <td className="text-end">
                        <button 
                          className="btn btn-sm btn-light btn-active-light-primary"
                          onClick={() => handleViewDetails(cashbox.id)}
                        >
                          Подробнее
                        </button>
                      </td>
                    </tr>
                  ))}
                </TableBody>
              </Table>

              {filteredAndSortedCashboxes.length === 0 && (
                <div className="d-flex justify-content-center py-10">
                  <div className="text-muted fs-4">
                    {searchTerm ? 'Кассы не найдены' : 'Список касс пуст'}
                  </div>
                </div>
              )}

              {countPages > 1 && (
                <div className="d-flex justify-content-center mt-6">
                  <ReactPaginate
                    previousLabel="‹"
                    nextLabel="›"
                    breakLabel="..."
                    pageCount={countPages}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageChange}
                    forcePage={currentPage - 1}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    activeClassName="active"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CreateCashBox
        show={showCreateModal}
        onHide={handleCloseCreateModal}
        onCashBoxCreated={handleCashBoxCreated}
        existingCashBoxes={cashboxes}
      />
    </div>
  )
}

export default CashboxesList 